import { useCallback, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { useI18n } from "../../i18n";
import { acceptInvite } from "../../api/collab";
import { ChannelsView } from "./ChannelsView";
import { ComputersView } from "./ComputersView";
import { MembersView } from "./MembersView";
import type { WorkspaceView } from "./AppRail";

function inviteTokenFromUrl(): string | null {
  return new URLSearchParams(window.location.search).get("invite");
}

function clearInviteFromUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete("invite");
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

/**
 * Workspace surface content (sidebar + main). The icon rail lives in App (shared
 * across surfaces); `view` selects the active sub-view. The invite banner floats
 * over the top so it doesn't disturb the rail/sidebar/main flex row.
 */
export function WorkspaceScreen({ view }: { view: WorkspaceView }) {
  const { account, baseUrl, client, reloadAccounts } = useAuth();
  const { t } = useI18n();
  const accountId = account?.id ?? "";
  const workspaceName = account?.name || t("collab.workspace.untitled");

  const [inviteToken, setInviteToken] = useState<string | null>(() => inviteTokenFromUrl());
  const [acceptedAccountId, setAcceptedAccountId] = useState<string | null>(null);
  const [joinState, setJoinState] = useState<"idle" | "joining" | "joined" | "error">("idle");

  const join = useCallback(async () => {
    if (!inviteToken || !client || joinState === "joining") return;
    setJoinState("joining");
    try {
      let joinedId = acceptedAccountId;
      if (!joinedId) {
        const accepted = await acceptInvite(client, inviteToken);
        joinedId = accepted.accountId;
        setAcceptedAccountId(joinedId);
      }
      await reloadAccounts(joinedId);
      setJoinState("joined");
      setAcceptedAccountId(null);
      clearInviteFromUrl();
      setInviteToken(null);
    } catch {
      setJoinState("error");
    }
  }, [acceptedAccountId, client, inviteToken, joinState, reloadAccounts]);

  return (
    <>
      {inviteToken ? (
        <div className="ws-invite-bar">
          <span>{t("collab.invite.banner")}</span>
          <button type="button" className="ws-btn" onClick={join} disabled={joinState === "joining"}>{t("collab.invite.join")}</button>
        </div>
      ) : null}
      {joinState === "joined" ? <div className="ws-invite-bar ok">{t("collab.invite.joined")}</div> : null}
      {joinState === "error" ? <div className="ws-invite-bar err">{t("collab.invite.failed")}</div> : null}

      {accountId ? (
        view === "chat" ? (
          <ChannelsView accountId={accountId} workspaceName={workspaceName} />
        ) : view === "members" ? (
          <MembersView accountId={accountId} workspaceName={workspaceName} />
        ) : (
          <ComputersView baseUrl={baseUrl} accountId={accountId} workspaceName={workspaceName} />
        )
      ) : (
        <div className="ws-content-empty">…</div>
      )}
    </>
  );
}
